import React, { useState } from "react";
import * as htmlToImage from "html-to-image";
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Checkbox,
  Button,
  Space,
  Card,
  Row,
  Col,
  Radio,
  Rate,
  message,
  Divider,
  QRCode,
  Modal,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  InstagramOutlined,
  WhatsAppOutlined,
  GlobalOutlined,
  MailOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getItinerary, saveItinerary } from "../services/api";
import Cookies from "js-cookie";
import "./itinerary.css";
import jsPDF from "jspdf";

const { TextArea } = Input;

export default function AntdItinerary() {
  const [form] = Form.useForm();
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState("");

  const accessToken = Cookies.get("token");
  const [messageApi, contextHolder] = message.useMessage();

  const downloadItineraryImage = async () => {
    setLoading(true);
    const node = document.getElementById("itinerary-image");
    if (!node) return;

    const height = node.scrollHeight;

    try {
      const dataUrl = await htmlToImage.toJpeg(node, {
        width: 1080,
        height,
        pixelRatio: 1.5, // 🔥 reduce quality (BIG SIZE DROP)
        quality: 0.85, // 🔥 compression 0.6–0.8 best
        backgroundColor: "#0a4d8c",
      });

      const pdf = new jsPDF("p", "px", [1080, height]);

      // 🔥 FAST + compressed
      pdf.addImage(dataUrl, "JPEG", 0, 0, 1080, height, undefined, "FAST");

      const fileName = `${previewData?.location || "Trip"} ${previewData?.nights}N ${previewData?.totalDays}D.pdf`;

      pdf.save(fileName);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("PDF generation failed", err);
    }
  };

  const validateItineraryJSON = (data) => {
    const requiredFields = [
      "itineraryId",
      "location",
      "startDate",
      "endDate",
      "nights",
      "totalDays",
      "days",
      "packages",
    ];

    const missing = requiredFields.filter(
      (field) =>
        data[field] === undefined || data[field] === null || data[field] === "",
    );

    return missing;
  };

  const handleValuesChange = (changedValues, allValues) => {
    const { startDate, nights } = allValues;

    if (
      changedValues.startDate !== undefined ||
      changedValues.nights !== undefined
    ) {
      if (startDate && nights !== undefined) {
        const calculatedEndDate = dayjs(startDate).add(nights, "day");
        const totalDaysCount = nights + 1;
        const currentDays = allValues.days || [];
        const newDays = [];

        for (let i = 0; i < totalDaysCount; i++) {
          const dateForDay = dayjs(startDate)
            .add(i, "day")
            .format("ddd, MMM D, YYYY");

          newDays.push(
            currentDays[i] || {
              title: `Day ${i + 1}`,
              date: dateForDay,
              meals: [],
              content: "",
            },
          );
        }

        form.setFieldsValue({
          endDate: calculatedEndDate,
          days: newDays,
          totalDays: totalDaysCount,
        });

        setPreviewData({
          ...allValues,
          endDate: calculatedEndDate,
          days: newDays,
          totalDays: totalDaysCount,
        });
      }
    } else {
      setPreviewData(allValues);
    }
  };

  const renderBullets = (text) => {
    if (!text) return null;
    return text
      .split("\n")
      .filter((l) => l.trim() !== "")
      .map((line, i) => <li key={i}>{line}</li>);
  }; // Inside your AntdItinerary component

  const handleImportJSON = () => {
    try {
      let raw = jsonInput.trim();

      if (!raw) {
        return messageApi.warning("Please paste itinerary JSON");
      }

      // Support GPT markdown code blocks
      raw = raw
        .replace(/^```json\s*/i, "")
        .replace(/^```\s*/i, "")
        .replace(/\s*```$/i, "")
        .trim();

      const parsed = JSON.parse(raw);

      const missing = validateItineraryJSON(parsed);

      if (missing.length > 0) {
        return messageApi.error(`Missing fields: ${missing.join(", ")}`);
      }

      if (!parsed.itineraryId) {
        return messageApi.error("itineraryId is missing");
      }

      if (!parsed.location) {
        return messageApi.error("location is missing");
      }

      // Convert ISO date strings into dayjs objects
      const formattedData = {
        ...parsed,
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null,
      };

      // Populate complete Ant Design form
      form.setFieldsValue(formattedData);

      // Update preview
      setPreviewData(formattedData);

      setJsonModalOpen(false);
      setJsonInput("");

      messageApi.success(
        `Itinerary ${parsed.itineraryId} imported successfully!`,
      );
    } catch (error) {
      console.error("JSON import failed:", error);

      messageApi.error("Invalid JSON. Please check the JSON generated by GPT.");
    }
  };
  const handleSaveData = async () => {
    try {
      const values = await form.validateFields();
      const id = values.itineraryId;

      if (!id) return messageApi.error("Please enter an Itinerary ID"); // Call the service function

      const result = await saveItinerary(id, values, accessToken);

      if (result.success) {
        messageApi.success("Itinerary saved successfully!");
      }
    } catch (error) {
      messageApi.error("Failed to save data. Check console for details.");
    }
  };

  const handleGetData = async () => {
    const id = form.getFieldValue("itineraryId");
    if (!id) return messageApi.warning("Enter an ID to fetch");

    try {
      const data = await getItinerary(id, accessToken);

      if (data) {
        // 🔁 Essential: Convert string dates back to dayjs for AntD
        const formattedData = {
          ...data,
          startDate: data.startDate ? dayjs(data.startDate) : null,
          endDate: data.endDate ? dayjs(data.endDate) : null,
        };

        form.setFieldsValue(formattedData);
        setPreviewData(formattedData);
        messageApi.success("Data loaded!");
      }
    } catch (error) {
      messageApi.error("No itinerary found with this ID.");
    }
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start", // ⭐ IMPORTANT FIX
        background: "#f0f2f5",
      }}
    >
      <style>{`
    @media print {
     .no-print { display: none !important; }
     .print-area { width: 100% !important; margin: 0 !important; padding: 0 !important; }
     body { background: white; }
     @page { margin: 0; size: auto; }
    }
   `}</style>
      {/* LEFT: ANTD ADMIN FORM */}
      <div
        className="no-print"
        style={{
          width: "40%",
          padding: "20px",
          overflowY: "auto",
          borderRight: "1px solid #d9d9d9",
          background: "#fff",
        }}
      >
        {contextHolder} <h2 style={{ color: "#003366" }}>Dive Hrzn Admin</h2>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleValuesChange}
          initialValues={{
            brand: "DIVE HRZN",
            nights: 1,
            paxMode: "fixed",
            days: [],
            packages: [{ optionName: "Option 1", rating: 3 }], // One mandatory package
          }}
        >
          <Card
            size="small"
            style={{
              marginBottom: 16,
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
            }}
          >
            <Space wrap>
              <Form.Item name="itineraryId" label="Itinerary ID" noStyle>
                <Input placeholder="e.g. GT-1093242" style={{ width: 200 }} />
              </Form.Item>

              <Button type="primary" onClick={handleGetData}>
                Get Data
              </Button>

              <Button type="dashed" onClick={() => setJsonModalOpen(true)}>
                Paste JSON
              </Button>

              <Button onClick={handleSaveData}>Save Data</Button>

              <Button
                type="primary"
                loading={loading}
                onClick={downloadItineraryImage}
              >
                Download Itinerary Image
              </Button>
            </Space>
          </Card>
          {/* Section 1: Basic Info */}
          <Card title="Basic Info" size="small" style={{ marginBottom: 16 }}>
            <Form.Item name="customerName" label="Customer Name">
              <Input placeholder="Mr. Suriya" />
            </Form.Item>
            <Form.Item name="location" label="Location">
              <Input placeholder="Bali" />
            </Form.Item>
            <Row gutter={4}>
              <Col span={12}>
                <Form.Item name="startDate" label="Start Date">
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="endDate" label="END Date">
                  <DatePicker disabled style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nights" label="Nights">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="totalDays" label="Days">
                  <InputNumber disabled min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          {/* Section 2: Pax Details */}
          <Card title="Pax Details" size="small" style={{ marginBottom: 16 }}>
            <Form.Item name="paxMode" label="Pax Entry Mode">
              <Radio.Group optionType="button" buttonStyle="solid">
                <Radio value="fixed">Fixed (Adults/Kids)</Radio>
                <Radio value="range">Range (Min/Max)</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.paxMode !== cur.paxMode}
            >
              {({ getFieldValue }) => {
                const mode = getFieldValue("paxMode");
                const label1 = mode === "fixed" ? "Adults" : "Min";
                const label2 = mode === "fixed" ? "Kids" : "Max";
                return (
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name="paxValue1" label={label1}>
                        <InputNumber min={1} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="paxValue2" label={label2}>
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Card>
          {/* Section 3: Itinerary */}
          <Card
            title="Day Wise Itinerary"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.List name="days">
              {(fields) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{
                        background: "#fafafa",
                        padding: 10,
                        marginBottom: 10,
                        borderRadius: 8,
                      }}
                    >
                      <Space
                        align="baseline"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "title"]}
                          rules={[{ required: true }]}
                        >
                          <Input
                            placeholder="Day Title"
                            style={{ fontWeight: "bold" }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "meals"]}
                          label="Meals"
                        >
                          <Checkbox.Group options={["B", "L", "D"]} />
                        </Form.Item>
                      </Space>
                      <Form.Item {...restField} name={[name, "content"]}>
                        <TextArea
                          rows={2}
                          placeholder="Activities (Enter for bullets)"
                        />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Card>
          <Card
            title="Inclusions & Exclusions"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.Item name="inclusions" label="Inclusions (Enter for bullets)">
              <TextArea
                rows={4}
                placeholder="e.g. Stay at selected hotel&#10;Private transport&#10;English speaking driver"
              />
            </Form.Item>
            <Form.Item name="exclusions" label="Exclusions (Enter for bullets)">
              <TextArea
                rows={4}
                placeholder="e.g. 1 Lunch and Dinner&#10;Flight Fare&#10;5% GST & 5% TCS"
              />
            </Form.Item>
          </Card>
          {/* Section: Package Options */}
          <Card
            title="Package Options"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.List name="packages">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} style={styles.packageBox}>
                      <Row gutter={8} align="middle">
                        <Col span={18}>
                          <Form.Item
                            {...restField}
                            name={[name, "optionName"]}
                            label="Package Name"
                            rules={[{ required: true }]}
                          >
                            <Input placeholder="e.g. Recommended" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          {index !== 0 && (
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            >
                              Remove
                            </Button>
                          )}
                        </Col>
                      </Row>
                      {/* Overall Package Price, Rating, and Basis */}
                      <Row gutter={8}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "optionRating"]}
                            label="Overall Rating"
                          >
                            <Rate style={{ fontSize: 14 }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "optionPrice"]}
                            label="Base Price"
                          >
                            <InputNumber style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        {/* NEW: Price Basis Switch/Radio */}
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "priceBasis"]}
                            label="Price Basis"
                            initialValue="per person"
                          >
                            <Radio.Group
                              optionType="button"
                              buttonStyle="solid"
                              size="small"
                            >
                              <Radio value="per person">Per Person</Radio>
                              <Radio value="per couple">Per Couple</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            name={[name, "optionNotes"]}
                            label="Option Note"
                          >
                            <TextArea placeholder="e.g. Superior Room Double Sharing" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <div
                        style={{
                          background: "#fff",
                          padding: "10px",
                          borderRadius: "6px",
                          border: "1px solid #d9d9d9",
                        }}
                      >
                        <Form.List name={[name, "properties"]}>
                          {(
                            propFields,
                            { add: addProp, remove: removeProp },
                          ) => (
                            <>
                              {propFields.map((propField, propIndex) => (
                                <div key={propField.key} style={styles.propRow}>
                                  <Row gutter={8}>
                                    <Col span={10}>
                                      <Form.Item
                                        {...propField}
                                        name={[propField.name, "propertyName"]}
                                        label="Hotel"
                                      >
                                        <TextArea />
                                      </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                      <Form.Item
                                        {...propField}
                                        name={[propField.name, "rating"]}
                                        label="Stars"
                                      >
                                        <Rate style={{ fontSize: 12 }} />
                                      </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                      <Form.Item
                                        {...propField}
                                        name={[propField.name, "hotelNights"]}
                                        label="Nights"
                                      >
                                        <InputNumber
                                          min={1}
                                          style={{ width: "100%" }}
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col span={2}>
                                      {propIndex !== 0 && (
                                        <DeleteOutlined
                                          onClick={() =>
                                            removeProp(propField.name)
                                          }
                                          style={{ marginTop: 30 }}
                                        />
                                      )}
                                    </Col>
                                    <Col span={24}>
                                      <Form.Item
                                        {...propField}
                                        name={[propField.name, "propertyNotes"]}
                                        label="Hotel Specific Notes"
                                      >
                                        <TextArea placeholder="e.g. Kuta - 4N" />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </div>
                              ))}
                              <Button
                                type="dashed"
                                size="small"
                                onClick={() =>
                                  addProp({
                                    propertyName: "",
                                    propertyNotes: "",
                                    rating: 3,
                                    hotelNights: 1,
                                  })
                                }
                                block
                                icon={<PlusOutlined />}
                              >
                                Add Hotel
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </div>
                      {/* THIS WAS MISSING - ADDED BACK NOW */}
                      <Form.Item
                        {...restField}
                        name={[name, "packageInclusions"]}
                        label="Package Specific Activities (Enter for bullets)"
                        style={{ marginTop: 15 }}
                      >
                        <TextArea
                          rows={3}
                          placeholder="Includes Jet Ski, Parasailing etc."
                        />
                      </Form.Item>
                    </div>
                  ))}
                  <Button
                    type="primary"
                    onClick={() => add({ properties: [{ rating: 3 }] })}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Package Option
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
          {/* NEW SECTION: Add-ons */}
          <Card
            title="Add-ons (Optional Extras)"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.List name="addons">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{
                        background: "#fff7e6", // Light orange for distinction
                        padding: 12,
                        marginBottom: 16,
                        borderRadius: 8,
                        border: "1px solid #ffd591",
                      }}
                    >
                      <Row gutter={8} align="middle">
                        <Col span={18}>
                          <Form.Item
                            {...restField}
                            name={[name, "addonTitle"]}
                            label="Add-on Title"
                          >
                            <Input placeholder="e.g. Scuba Photography / VIP Lounge" />
                          </Form.Item>
                        </Col>
                        <Col span={6} style={{ textAlign: "right" }}>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: 25 }}
                          >
                            Remove
                          </Button>
                        </Col>
                      </Row>
                      <Row gutter={8}>
                        <Col span={16}>
                          <Form.Item
                            {...restField}
                            name={[name, "addonContent"]}
                            label="Description (Enter for bullets)"
                          >
                            <TextArea
                              rows={2}
                              placeholder="Detail the service here..."
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "addonPrice"]}
                            label="Price (Optional)"
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="INR"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        addonTitle: "",
                        addonContent: "",
                        addonPrice: null,
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                  >
                    Add New Add-on
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
          <Card
            title="Payment & Bank Details"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              name="bankDetails"
              label="Bank Details (NEFT/IMPS)"
              initialValue={`Account no : 69450200001471\nAcc name : DIVE HRZN\nAccount type : CURRENT ACCOUNT\nBank : BOB, VIJAYA NAGAR VELCHERY, CHENNAI\nIFSC CODE : BARB0VJVELA`}
            >
              <TextArea rows={5} />
            </Form.Item>
            <Form.Item name="qrUrl" label="UPI QR Code URL">
              <Input placeholder="https://url-to-your-qr-image.png" />
            </Form.Item>
            <Divider orientation="left" style={{ fontSize: "12px" }}>
              Payment Offers
            </Divider>
            <Form.List name="paymentOffers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item {...restField} name={[name, "offer"]} noStyle>
                        <Input
                          placeholder="e.g. 1800 instant discount on UPI"
                          style={{ width: "250px" }}
                        />
                      </Form.Item>
                      <DeleteOutlined
                        onClick={() => remove(name)}
                        style={{ color: "red" }}
                      />
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        offer: "",
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                    size="small"
                  >
                    Add Offer
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
          <Card
            title="Policies & Notes"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              name="paymentPolicy"
              label="Payment Policy (Enter for bullets)"
            >
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item
              name="cancellationPolicy"
              label="Cancellation Policy (Enter for bullets)"
            >
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item
              name="generalAwareness"
              label="General Awareness for Travelers (Read Carefully)"
            >
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item
              name="importantNotes"
              label="Important Notes (Read Carefully)"
            >
              <TextArea rows={2} />
            </Form.Item>
          </Card>
          {/* Section 6: Payment */}
          <Card title="6) photos" size="small" style={{ marginBottom: 16 }}>
            <Form.Item name="driveLink" label="Drive Link">
              <Input />
            </Form.Item>
          </Card>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            block
            size="large"
            onClick={() => window.print()}
            style={{ background: "#003366" }}
          >
            Generate PDF
          </Button>
        </Form>
        <Modal
          title="Import Itinerary JSON"
          open={jsonModalOpen}
          onCancel={() => {
            setJsonModalOpen(false);
            setJsonInput("");
          }}
          onOk={handleImportJSON}
          okText="Import JSON"
          width={800}
        >
          <p style={{ color: "#666" }}>
            Paste the JSON generated by ChatGPT below. You can paste it directly
            even if it is inside a<code>```json</code> code block.
          </p>

          <TextArea
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder={`Paste itinerary JSON here...

        Example:
        {
          "itineraryId": "DZ-MUNNAR",
          "location": "Munnar",
          "startDate": "2026-09-07T18:30:00.000Z",
          ...
        }`}
            rows={25}
            style={{
              fontFamily: "monospace",
              fontSize: 13,
            }}
          />
        </Modal>
      </div>
      {/* RIGHT: PDF PREVIEW */}
      <div id="itinerary-image" className="itinerary-canvas">
        <div className="itinerary-bg" />
        <div className="itinerary-root">
          {!previewData && (
            <div style={{ padding: "40px", textAlign: "center" }}>
              Loading itinerary…
            </div>
          )}

          {previewData && (
            <>
              {/* HEADER */}
              {/* HERO / COVER */}
              <div className="itinerary-hero">
                {/* BRAND TEXT */}
                <div className="hero-brand-text">DIVE HRZN</div>

                {/* TITLE */}
                <h1 className="hero-title">
                  Hello{" "}
                  <span className="hero-highlight">
                    {previewData?.customerName || "Traveler"}
                  </span>
                  , are you ready for{" "}
                  <span className="hero-highlight">
                    {previewData?.location}
                  </span>
                  ?
                </h1>
              </div>

              {/* SUMMARY */}
              <div className="summary-bar">
                {/* Location */}
                <div className="summary-item">
                  <span className="icon">📍</span>
                  <div>
                    <div className="label">Location</div>
                    <div className="value">{previewData?.location || "-"}</div>
                  </div>
                </div>

                {/* Dates */}
                <div className="summary-item">
                  <span className="icon">📅</span>
                  <div>
                    <div className="label">Dates</div>
                    <div className="value">
                      {previewData?.startDate
                        ? dayjs(previewData?.startDate).format("DD MMM")
                        : "--"}{" "}
                      -{" "}
                      {previewData?.endDate
                        ? dayjs(previewData?.endDate).format("DD MMM")
                        : "--"}
                    </div>
                  </div>
                </div>

                {/* Nights / Days */}
                <div className="summary-item">
                  <span className="icon">🌙</span>
                  <div>
                    <div className="label">Duration</div>
                    <div className="value">
                      {previewData?.nights}N / {previewData?.totalDays}D
                    </div>
                  </div>
                </div>

                {/* Pax */}
                <div className="summary-item">
                  <span className="icon">👥</span>
                  <div>
                    <div className="label">Guests</div>
                    <div className="value">
                      {(() => {
                        const v1 = previewData?.paxValue1;
                        const v2 = previewData?.paxValue2;

                        if (previewData?.paxMode === "fixed") {
                          const parts = [];
                          if (v1) parts.push(`${v1} Adults`);
                          if (v2) parts.push(`${v2} Kids`);
                          return parts.join(" + ");
                        }
                        if (v1 && v2) return `Min ${v1} - Max ${v2}`;
                        if (v1) return `Min ${v1}`;
                        if (v2) return `Max ${v2}`;
                        return "-";
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* ITINERARY */}
              <div className="itinerary-box">
                {/* attached title */}
                <div className="itinerary-title"> DAY WISE ITINERARY</div>

                <div className="itinerary-cards">
                  {previewData?.days?.map((day, i) => (
                    <div key={i} className="day-card-new">
                      <div className="day-header-yellow">
                        <span className="day-number">
                          DAY {String(i + 1).padStart(2, "0")}
                        </span>

                        <span className="day-title">{day.title}</span>

                        <span className="meals">
                          {day.meals?.includes("B") && "B "}
                          {day.meals?.includes("L") && "L "}
                          {day.meals?.includes("D") && "D"}
                        </span>
                      </div>

                      <ul className="day-list">{renderBullets(day.content)}</ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* STAY OPTIONS */}

              <div className="stay-box">
                <div className="stay-title">PACKAGES</div>

                <div className="stay-grid">
                  {/* PACKAGES */}
                  {previewData?.packages?.map((pkg, i) => (
                    <div key={i} className="stay-card">
                      <div className="stay-option-header">
                        <div>
                          <div className="stay-option-name">
                            {pkg.optionName}
                          </div>
                          {pkg.optionNotes && (
                            <div className="option-notes multiline-text">
                              {pkg.optionNotes}
                            </div>
                          )}
                        </div>

                        <div className="stay-option-meta">
                          <span className="stars">
                            {"★".repeat(pkg.optionRating || 0)}
                          </span>

                          {pkg.optionPrice && (
                            <span className="price">
                              ₹{pkg.optionPrice} {pkg.priceBasis}
                            </span>
                          )}
                        </div>
                      </div>

                      {pkg.properties?.filter(Boolean).map((prop, pi) => (
                        <div key={pi} className="hotel-row">
                          <div>
                            <div className="hotel-name multiline-text">
                              {prop.propertyName || ""}
                            </div>
                            <div className="hotel-location multiline-text">
                              {prop.propertyNotes || ""}
                            </div>
                          </div>

                          <div className="hotel-right">
                            <span className="hotel-stars">
                              {"★".repeat(prop.rating || 0)}
                            </span>
                            <span className="nights">
                              {prop.hotelNights || 0}N
                            </span>
                          </div>
                        </div>
                      ))}

                      {pkg.packageInclusions && (
                        <div className="pkg-inclusions multiline-text">
                          • {pkg.packageInclusions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* 🔥 ADD-ONS CARD (single card in grid) */}
              {previewData?.addons?.length > 0 && (
                <div className="addon-card-wide">
                  <div className="addon-mini-title">ADD-ONS</div>

                  {previewData?.addons?.filter(Boolean).map((addon, i) => (
                    <div key={i} className="addon-row">
                      <div>
                        <div className="addon-name">
                          {addon?.addonTitle || ""}
                        </div>
                        <div className="addon-content multiline-text">
                          {addon?.addonContent || ""}
                        </div>
                      </div>

                      {addon?.addonPrice && (
                        <span className="addon-price-badge">
                          ₹{addon.addonPrice}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* PAYMENT */}
              <div className="payment-section">
                <div className="payment-title">PAYMENT & BANK DETAILS</div>

                <div className="payment-grid">
                  {/* LEFT */}
                  <div className="payment-card bank-card">
                    <div className="payment-subtitle">
                      BANK TRANSFER (NEFT / IMPS)
                    </div>
                    <div className="bank-text">
                      {previewData?.bankDetails
                        ?.split("\n")
                        .filter(Boolean)
                        .map((line, i) => {
                          const [label, value] = line.split(":");
                          return (
                            <div key={i} className="bank-row">
                              <span className="bank-label">{label}</span>
                              <span className="bank-sep">:</span>
                              <span className="bank-value">{value}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* RIGHT */}
                  <div className="payment-card qr-card">
                    <div className="payment-subtitle">QR CODE</div>

                    <QRCode errorLevel="H" value={previewData?.qrUrl} />
                    {/* {previewData?.upiId && ( */}
                    <div className="upi-box">
                      <div className="upi-left">
                        <span className="upi-label">UPI ID</span>
                        <span className="upi-text">
                          dive70929471@barodampay
                        </span>
                      </div>
                    </div>
                    {/* )} */}
                  </div>
                </div>
                {/* PAYMENT MODES */}
                <div className="payment-modes-bar">
                  <span className="modes-label">Payment Modes Available</span>

                  <div className="modes-list">
                    <span className="mode-pill">UPI</span>
                    <span className="mode-pill">BHIM</span>
                    <span className="mode-pill">GPay</span>
                    <span className="mode-pill">Paytm</span>
                    <span className="mode-pill">Credit Card</span>
                    <span className="mode-pill">Debit Card</span>
                    <span className="mode-pill">EMI</span>
                    <span className="mode-pill">NEFT</span>
                    <span className="mode-pill">IMPS</span>
                  </div>
                </div>

                {/* 🔥 OFFERS — OUTSIDE PAYMENT */}
                {previewData?.paymentOffers?.length > 0 && (
                  <div className="payment-offer-strip">
                    {previewData?.paymentOffers?.filter(Boolean).map((o, i) => (
                      <span key={i} className="offer-pill">
                        {o.offer}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="inc-exc-wrapper">
                {/* INCLUSIONS */}
                <div className="inc-card">
                  <h3 className="inc-title">✅ INCLUSIONS</h3>
                  <ul className="inc-list">
                    {renderBullets(previewData?.inclusions)}
                  </ul>
                </div>

                {/* EXCLUSIONS */}
                <div className="exc-card">
                  <h3 className="exc-title">❌ EXCLUSIONS</h3>
                  <ul className="exc-list">
                    {renderBullets(previewData?.exclusions)}
                  </ul>
                </div>
              </div>
              {/* POLICIES */}
              <div className="policy-box">
                <div className="policy-title">POLICIES & IMPORTANT NOTES</div>

                <div className="policy-grid">
                  <div className="policy-card">
                    <div className="policy-subtitle">PAYMENT POLICY</div>
                    <ul className="policy-list">
                      {renderBullets(previewData?.paymentPolicy)}
                    </ul>
                  </div>

                  <div className="policy-card">
                    <div className="policy-subtitle">CANCELLATION POLICY</div>
                    <ul className="policy-list">
                      {renderBullets(previewData?.cancellationPolicy)}
                    </ul>
                  </div>

                  <div className="policy-card">
                    <div className="policy-subtitle">NOTES</div>
                    <ul className="notes-policy-list">
                      {renderBullets(previewData?.importantNotes)}
                    </ul>
                  </div>

                  <div className="policy-card">
                    <div className="policy-subtitle">GENERAL AWARENESS</div>
                    <ul className="ga-policy-list">
                      {renderBullets(previewData?.generalAwareness)}
                    </ul>
                  </div>
                </div>
              </div>

              {/* DRIVE LINK */}
              {/* DRIVE LINK */}
              {previewData?.driveLink && (
                <div className="drive-box">
                  <div className="drive-title">PHOTOS & MEDIA</div>

                  <a
                    href={previewData?.driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="drive-card"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/d/da/Google_Drive_logo.png"
                      alt="Drive"
                      className="drive-icon"
                    />

                    <div className="drive-text">
                      <div className="drive-main">View Trip Photos</div>
                      <div className="drive-sub">Open Google Drive Folder</div>
                    </div>

                    <span className="drive-arrow">→</span>
                  </a>
                </div>
              )}
              {/* THANK YOU PAGE */}
              {/* THANK YOU PAGE */}
              <div className="thankyou-section">
                {/* brand */}
                <div className="thankyou-brand">DIVE HRZN</div>

                {/* big thank you */}
                <div className="thankyou-title">Thank You</div>

                {/* contacts bottom right */}
                {/* footer contacts */}
                <div className="thankyou-footer">
                  <InstagramOutlined /> divehrzn
                  <span className="footer-sep">|</span>
                  <WhatsAppOutlined /> +91 7092925555
                  <span className="footer-sep">|</span>
                  <GlobalOutlined /> www.divehrzn.com
                  <span className="footer-sep">|</span>
                  <MailOutlined /> contact@divehrzn.com
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
const styles = {
  mainLayout: { display: "flex", height: "100vh", background: "#f0f2f5" },
  adminSide: {
    width: "40%",
    padding: "20px",
    overflowY: "auto",
    borderRight: "1px solid #d9d9d9",
    background: "#fff",
  },
  previewSide: {
    width: "60%",
    padding: "40px",
    overflowY: "auto",
    background: "#525659",
  },
  pdfPaper: {
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    background: "white",
    minHeight: "100vh",
    boxShadow: "0 0 10px rgba(0,0,0,0.5)",
  },
  pdfHeader: {
    background: "#003366",
    color: "white",
    padding: "30px",
    textAlign: "center",
  },
  apiCard: {
    marginBottom: 16,
    background: "#f6ffed",
    border: "1px solid #b7eb8f",
  },
  card: { marginBottom: 16 },
  packageBox: {
    background: "#f0f5ff",
    padding: 12,
    marginBottom: 16,
    borderRadius: 8,
    border: "1px solid #adc6ff",
  },
  propRow: {
    background: "#fff",
    padding: 8,
    borderRadius: 4,
    marginBottom: 8,
    border: "1px solid #eee",
  },
};
